import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { usePoll } from '../../hooks/usePoll';
import { useHistory } from 'react-router-dom';

import { Badge, Segmented } from '../../components/ui';
import { audienceOf, deviceFrom, featureLabel, initials, pageLabel, timeAgo, typeLabel } from './usageInsights';

// "Live now": everyone with a signed-in API call in the last N minutes, across
// every store — where they are (latest endpoint) and on what (device).
// GET /api/admin/usage-analytics/live, polled every 30 s while the tab is visible.

const LIVE_WINDOWS = [
  { value: '5', label: '5 min' },
  { value: '15', label: '15 min' },
  { value: '60', label: '1 h' },
];
const POLL_MS = 30000;
const ALL = '__all';

export default function LiveNowPanel() {
  const history = useHistory();
  const [minutes, setMinutes] = useState('5');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [store, setStore] = useState(ALL);
  const [, setTick] = useState(0); // re-render so "x s ago" stays current

  // usePoll (M4.1) runs the first load at once, re-asks on return to the tab,
  // backs off on failure and stops on 401/403/404 (the 404 text stays up).
  // The inline error text is set exactly as before.
  const minutesRef = useRef(minutes);
  minutesRef.current = minutes;
  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/usage-analytics/live', { params: { minutes }, metadata: { silent: true } });
      if (minutesRef.current === minutes) { setData(res.data); setError(''); }
    } catch (e) {
      if (minutesRef.current === minutes) {
        setError(e.response?.status === 404
          ? 'Live view arrives with the next backend release.'
          : 'Could not load who is online.');
      }
      throw e;
    }
  }, [minutes]);
  usePoll(load, { interval: POLL_MS, immediate: true });

  useEffect(() => {
    const clock = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(clock);
  }, []);

  const users = useMemo(() => data?.users || [], [data]);
  const stores = useMemo(() => {
    const counts = new Map();
    users.forEach((u) => {
      const key = u.store_name || 'No store';
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [users]);
  const shown = store === ALL ? users : users.filter((u) => (u.store_name || 'No store') === store);
  const count = (a) => users.filter((u) => audienceOf(u) === a).length;

  return (
    <section className="ua-live" aria-labelledby="ua-live-title">
      <header className="ua-live__head">
        <div className="ua-live__title">
          <span className={`ua-pulse${users.length ? ' is-on' : ''}`} aria-hidden="true" />
          <h2 id="ua-live-title">Live now</h2>
          <span className="ua-live__count">
            {data ? `${users.length} online · ${count('merchant')} merchants · ${count('client')} clients · ${count('team')} team` : 'Loading…'}
          </span>
        </div>
        <Segmented label="Active within" size="sm" options={LIVE_WINDOWS} value={minutes} onChange={(v) => { setMinutes(v); setStore(ALL); }} />
      </header>

      {error && <p className="ua-empty">{error}</p>}

      {!error && data && users.length === 0 && (
        <p className="ua-empty">Nobody has used the app in the last {minutes === '60' ? 'hour' : `${minutes} minutes`}.</p>
      )}

      {!error && users.length > 0 && (
        <>
          {stores.length > 1 && (
            <div className="ua-chips" role="group" aria-label="Filter live users by store">
              <button type="button" className={`ua-chip${store === ALL ? ' is-on' : ''}`} aria-pressed={store === ALL} onClick={() => setStore(ALL)}>
                All stores <b>{users.length}</b>
              </button>
              {stores.map(([name, count]) => (
                <button key={name} type="button" className={`ua-chip${store === name ? ' is-on' : ''}`} aria-pressed={store === name} onClick={() => setStore(name)}>
                  {name} <b>{count}</b>
                </button>
              ))}
            </div>
          )}

          <ul className="ua-live__list">
            {shown.map((u) => (
              <li key={u.user_id}>
                <button type="button" className="ua-person" onClick={() => history.push(`/usage-analytics/users/${u.user_id}`)}>
                  <span className={`ua-avatar${u.is_team ? ' is-team' : ''}`} aria-hidden="true">{initials(u.name)}</span>
                  <span className="ua-person__main">
                    <span className="ua-person__name">
                      {u.name || `User #${u.user_id}`}
                      <Badge tone={u.is_team ? 'accent' : 'muted'} size="sm">{typeLabel(u)}</Badge>
                      {u.is_script && <Badge tone="info" size="sm">Script</Badge>}
                    </span>
                    <span className="ua-person__meta">{u.store_name || 'No store'} · {deviceFrom(u.user_agent, u.is_script)}</span>
                    <span className="ua-person__where" title={u.last_page || u.last_endpoint || undefined}>On {u.last_page ? pageLabel(u.last_page) : featureLabel(u.feature)}</span>
                  </span>
                  <span className="ua-person__side">
                    <span className="ua-person__ago">{timeAgo(u.last_at)}</span>
                    <span className="ua-person__calls">{u.calls} call{u.calls === 1 ? '' : 's'}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
